import autobind from 'class-autobind';
import config from 'config';
import noop from 'lodash/noop';

import BaseDataObject from 'atg/api/BaseDataObject';
import { bet as betSelectors } from 'atg/redux/selectors';
import { getStore } from 'shared/redux/store';
import Sentry from 'shared/utils/sentry';

import RequestError from './RequestError';

let unauthorizedHandler = noop;
let defaultErrorHandler = noop;

const parseHeaders = responseHeaders => {
  const headers = {};
  responseHeaders.forEach((value, name) => {
    headers[name] = value;
  });
  return headers;
};

export async function promiseRequest({
  path,
  data,
  method = 'GET',
  headers: customHeaders = {},
}) {
  let url = path;
  if (!path.includes('://')) {
    url = `${config.jokerApi}/${url}`;
  }

  if (process.env.NODE_ENV === 'development') {
    console.info(`REQUEST: ${path} ${method}`);
  }

  const betId = betSelectors.getCurrentBetId(getStore().getState());

  const headers = {
    'Content-Type': 'application/json',
    ...customHeaders,
  };

  if (betId != null) {
    headers['betId'] = betId;
  }

  const response = await fetch(url, {
    credentials: 'include',
    headers,
    method,
    body: method === 'POST' ? JSON.stringify(data) : undefined,
  });

  if (process.env.NODE_ENV === 'development') {
    console.info(`RESPONSE: ${path}`);
  }

  if (response.status < 200 || response.status >= 300) {
    const err = new Error(`${path}: ${response.status} ${response.statusText}`);
    err.response = response;
    throw err;
  }

  return response;
}

export default class Request {
  static setUnauthorizedHandler(handler) {
    unauthorizedHandler = handler || noop;
  }

  static setDefaultErrorHandler(handler) {
    defaultErrorHandler = handler || noop;
  }

  constructor(data) {
    autobind(this);

    if (data.retry !== false) {
      this.originalData = data;
    }

    for (const name in data) {
      if (!this[name]) {
        this[name] = data[name];
      }
    }

    this.createdTime = Date.now();
    this.completed = false;
    this.headers = this.headers || {};

    if (this.data instanceof BaseDataObject) {
      this.data = this.data.data;
    }
  }

  async start() {
    let success = false;

    try {
      const response = await promiseRequest(this);

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        try {
          this.responseData = await response.json();
        } catch (error) {
          Sentry.captureException(error);
          this.responseData = {};
        }
      } else {
        this.responseData = await response.text();
      }

      this.responseHeaders = parseHeaders(response.headers);
      this.status = response.status;
      this.completed = true;
      success = true;
    } catch (error) {
      // TypeError is thrown by fetch if the request was malformed
      // or there was a network error
      const isTypeError = error instanceof TypeError;
      const { response = {} } = error;
      const { status = 0, statusText = '', headers = [] } = response;
      const responseText = error.response ? await response.text() : '';

      if (process.env.NODE_ENV === 'development') {
        console.error(
          `${error} ${responseText.length > 0 ? `– ${responseText}` : ''}`,
        );
      }

      this.responseData = null;
      this.responseHeaders = parseHeaders(headers);
      this.status = status;
      this.completed = true;

      this.error = isTypeError
        ? error
        : new RequestError(this, status, statusText, responseText);

      if (status === 401) {
        unauthorizedHandler(this);
      } else {
        let captured = false;
        if (this.onError && !isTypeError) {
          captured = !!this.onError(this);
        }

        if (!captured) {
          defaultErrorHandler(this);
        }
      }
    }

    if (success && this.onSuccess) {
      this.onSuccess(this);
    }

    return this;
  }
}
